import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, InlineAlert } from '../components/BaseComponents'
import { FormEvent, useEffect, useState } from 'react';
import useApi from '../hooks/useApi';
import { FORM_INPUT_TEXT_CLASS, FORM_LABEL_CLASS } from '../lib/formStyles';
import { INPUT_FOCUS_RING, TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus';
import { getCSRFToken } from '../lib/utils';
import { getNewPasswordError, PASSWORD_HINT } from '../lib/passwordPolicy';

function ResetPassword() {
    const api = useApi();
    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');

    const [qs] = useSearchParams();
    // Held in state and stripped from the address bar so the token doesn't linger in history,
    // screenshots or Referer headers.
    const [resetToken] = useState(() => qs.get('resetToken'));
    const { pathname } = useLocation();
    useEffect(() => {
        if (qs.has('resetToken')) {
            navigate(pathname, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFormSubmit = (e: FormEvent) => {
       e.preventDefault();
       onSubmit();
       return false; 
    }

    const onSubmit = async () => {
        const passwordError = getNewPasswordError(newPassword, confirmPassword)
        if (passwordError) {
            setError(passwordError)
            return
        }

        setSubmitting(true)
        setError('');

        try {
            await api.post('/user/resetPassword', {
                password: newPassword,
                token: resetToken,
                _csrf: await getCSRFToken(),
            });

            navigate('/login', {
                replace: true,
                state: { message: 'Your password has been updated, please log in below.' },
            })
        } catch(error: any) {
            setSubmitting(false);
            if(error.body) {
                setError(error.body.error)
            } else {
                setError('An unknown error occured');
            }
        }
    }

    return (
        <div className="content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16 mb-8">
            <div className="text-charcoal-100 text-2xl text-center">Reset Password</div>

                <>
                    <form onSubmit={onFormSubmit}>
                        <label className="block mt-8 text-sm">
                            <span className={FORM_LABEL_CLASS}>Enter your new password</span>
                            <br />
                            <input
                                disabled={submitting}
                                className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                                type="password"
                                autoComplete="new-password"
                                placeholder="Create a new password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                            />
                            <span className='block mt-1 text-xs text-charcoal-100'>{PASSWORD_HINT}</span>
                        </label>
                        <label className="block mt-8 text-sm">
                            <span className={FORM_LABEL_CLASS}>Confirm password</span>
                            <br />
                            <input
                                id="confirmPasswordField"
                                disabled={submitting}
                                className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                                type="password"
                                autoComplete="new-password"
                                placeholder="Confirm your password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                            />
                        </label>
                        <div className='mt-6 flex justify-start'>
                            <Button
                                disabled={submitting}
                                buttonType="submit"
                                type="Primary"
                                text="Reset Password"
                                context="form-field"
                            />
                        </div>

                        <div className="mt-6 text-charcoal-100">
                            <Link
                                className={`text-link text-charcoal-100 ${TEXT_LINK_FOCUS_RING}`}
                                to="/login"
                            >
                                Log in
                            </Link>
                        </div>
                    </form>
                    { error && (
                        <InlineAlert type='error' className='mt-6 whitespace-pre-line'>{ error }</InlineAlert>
                    )}
                </>
        </div>
    );
}

export default ResetPassword
